import { FileClock, HardHat, Pencil, Plus, Search, Upload, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import { normalizeName } from '@obraplan/core/domain/shared/naming';
import { formatQuarterHours } from '@obraplan/core/domain/calendar/capacity';
import { Cpf } from '@obraplan/core/domain/workforce/cpf';
import {
  EMPLOYEE_STATUS_LABEL,
  type EmployeeStatus,
} from '@obraplan/core/domain/workforce/employee';
import {
  employeeHourlyRate,
  monthlyCostWithCharges,
} from '@obraplan/core/domain/workforce/labor-cost';
import { Hours } from '@obraplan/core/domain/shared/hours';
import type { JobRoleId } from '@obraplan/core/domain/shared/ids';
import { ok } from '@obraplan/core/domain/shared/result';
import {
  RESOURCE_TYPE_LABEL,
  SERVICE_PRICING_LABEL,
} from '@obraplan/core/domain/workforce/execution-resource';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Section } from '@/components/ui/section';
import { TabList, TabPanel } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Figure } from '@/features/reports/Figure';
import { KpiStrip } from '@/features/reports/KpiStrip';
import type { KpiItem } from '@/features/reports/kpis';
import { cn } from '@/lib/cn';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/states';
import {
  RoleDisciplinesPanel,
  type RoleDisciplinesChange,
} from '@/features/workforce/RoleDisciplinesPanel';
import {
  custoDaEquipePorHora,
  custoDaFolha,
  numeroNoCampo,
  mesPorExtenso,
  semEncargoInformado,
  textoDoEncargo,
} from '@/features/workforce/custo-da-folha';
import {
  EmployeeImportDialog,
  type EmployeeImportOptions,
} from '@/features/workforce/EmployeeImportDialog';
import type { EmployeeColumnMapping } from '@obraplan/core/domain/workforce/employee-import';
import type { EmployeeImportPreview } from '@obraplan/core/application/use-cases/workforce/employee-import-use-cases';
import {
  EmployeeDialog,
  JobRoleDialog,
  ServiceDialog,
  TeamDialog,
  type EmployeeSubmit,
  type ServiceSubmit,
} from '@/features/workforce/WorkforceDialogs';
import { TeamDisciplinesDialog } from '@/features/workforce/TeamDisciplinesDialog';
import { teamDisciplinesSummary } from '@/features/workforce/team-disciplines';
import { teamMoveNotice } from '@/features/planning/team-allocation';
import {
  useContainer,
  usePermission,
  useSessionState,
  useVisibleTabs,
} from '@/features/session/use-session';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';

const STATUS_TONE: Readonly<Record<EmployeeStatus, 'ok' | 'warn' | 'neutral'>> = {
  ACTIVE: 'ok',
  ON_LEAVE: 'warn',
  TERMINATED: 'neutral',
};

const STATUS_DOT: Readonly<Record<EmployeeStatus, string>> = {
  ACTIVE: 'bg-(--color-state-ok)',
  ON_LEAVE: 'bg-(--color-state-warn)',
  TERMINATED: 'bg-(--color-state-neutral)',
};

/** O ponto ao lado do seletor de situação. Decorativo: quem diz a situação é o texto. */
/**
 * "Disciplinas que executa" no cartão da equipe (Fase 30, RN-431): as que valem hoje e, sem
 * marcação, o aviso de que são o padrão. O botão abre a lista de caixas — para quem não edita,
 * só para ler.
 */
function TeamDisciplinesSection({
  teamName,
  names,
  isDefault,
  onOpen,
}: {
  teamName: string;
  names: readonly string[];
  isDefault: boolean;
  onOpen: () => void;
}) {
  return (
    <section
      aria-label={`Disciplinas que ${teamName} executa`}
      className="mt-3 flex flex-col gap-1.5 border-t border-(--color-border-subtle) pt-3"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-(--color-content-muted)">
          Disciplinas que executa
        </p>
        <Button
          variant="ghost"
          size="sm"
          className="h-11 md:h-8"
          aria-label={`Disciplinas de ${teamName}`}
          onClick={onOpen}
        >
          <HardHat aria-hidden="true" />
          {isDefault ? 'Marcar' : 'Editar'}
        </Button>
      </div>
      {names.length === 0 ? (
        <p className="text-sm text-(--color-content-muted)">
          {isDefault
            ? 'Nenhuma: mais da metade da equipe não tem disciplina em comum. Marque as que ela executa.'
            : 'Nenhuma disciplina ativa marcada.'}
        </p>
      ) : (
        <ul className="flex flex-wrap gap-1">
          {names.map((name) => (
            <li key={name} className="min-w-0 max-w-full">
              <Badge tone={isDefault ? 'neutral' : 'info'}>
                <span className="truncate">{name}</span>
              </Badge>
            </li>
          ))}
        </ul>
      )}
      {isDefault && (
        <p className="text-xs text-(--color-content-muted)">
          Padrão: o que mais da metade da equipe executa
        </p>
      )}
    </section>
  );
}

function StatusDot({ status }: { status: EmployeeStatus }) {
  return (
    <span aria-hidden="true" className={cn('size-2 shrink-0 rounded-full', STATUS_DOT[status])} />
  );
}

/**
 * O seletor que só se mostra como campo ao passar o mouse ou receber o foco.
 *
 * Dois por linha, numa lista de vinte pessoas, eram quarenta bordas: a tabela parecia um
 * formulário, e o olho não achava o nome. O controle continua lá, com o mesmo nome para o
 * leitor de tela — só a moldura espera o gesto.
 */
const QUIET_SELECT =
  '-ml-2 h-8 max-w-44 truncate rounded-md border border-transparent bg-transparent px-2 text-sm text-(--color-content) transition-colors hover:border-(--color-border-subtle) hover:bg-(--color-surface) focus:border-(--color-border-subtle) focus:bg-(--color-surface)';

type Tab = 'employees' | 'teams' | 'services' | 'roles';

const TABS: readonly { id: Tab; label: string }[] = [
  { id: 'employees', label: 'Funcionários' },
  { id: 'teams', label: 'Equipes' },
  { id: 'services', label: 'Serviços terceirizados' },
  { id: 'roles', label: 'Funções e disciplinas' },
];

/**
 * Quem executa a obra. Funcionário e serviço terceirizado convivem aqui porque o
 * planejamento aloca os dois pelo mesmo mecanismo (ADR-009); o serviço não é material e
 * não aparece em tela de compra (RN-056).
 */
export function WorkforcePage() {
  const container = useContainer();
  const { session, activeProjectId, selectProject } = useSessionState();
  const podeGerir = usePermission(PERMISSION.EMPLOYEES_MANAGE);

  // Só as abas marcadas no papel (RN-626). A escolhida que ele não vê — a primeira, por
  // padrão — cai na primeira que ele vê (RN-629).
  const abasDoPapel = useVisibleTabs('/mao-de-obra');
  const tabs = TABS.filter((item) => abasDoPapel.some((aba) => aba.id === item.id));
  const [tabEscolhida, setTab] = useState<Tab>('employees');
  const tab = tabs.some((item) => item.id === tabEscolhida)
    ? tabEscolhida
    : (tabs[0]?.id ?? tabEscolhida);
  const [search, setSearch] = useState('');
  const [employeeOpen, setEmployeeOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [serviceOpen, setServiceOpen] = useState(false);
  // `undefined` com o diálogo aberto = cadastro novo. Guardo o id, não o registro: a lista
  // é reativa, e uma cópia congelada mostraria o estado de antes da última gravação.
  const [editingEmployeeId, setEditingEmployeeId] = useState<string | undefined>(undefined);
  const [editingTeamId, setEditingTeamId] = useState<string | undefined>(undefined);
  // A equipe cujas "Disciplinas que executa" estão abertas (Fase 30, RN-433).
  const [disciplinesTeamId, setDisciplinesTeamId] = useState<string | undefined>(undefined);
  const [editingServiceId, setEditingServiceId] = useState<string | undefined>(undefined);
  const [importOpen, setImportOpen] = useState(false);
  const [preview, setPreview] = useState<EmployeeImportPreview | undefined>(undefined);
  // Os bytes ficam aqui para que trocar o mapeamento não exija reabrir o arquivo.
  const [fileBytes, setFileBytes] = useState<{ bytes: Uint8Array; name: string } | undefined>(
    undefined,
  );

  const view = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.getWorkforce.execute(activeProjectId, session),
    [session?.userId, activeProjectId],
  );

  // O catálogo de unidades: o terceirizado cobrado por produção contrata uma delas.
  const units = useLive(() => container.listUnitsOfMeasure.execute(), []);

  const calendar = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.getWorkCalendar.execute(activeProjectId, session),
    [session?.userId, activeProjectId],
  );

  const data = view !== undefined && view.ok ? view.value : undefined;
  // A versão mostra a lista da obra aprovada, só para ler (Fase 36, RN-461, RN-462): os
  // funcionários são os mesmos em todas as versões, e quem os cadastra é a aprovada.
  const daAprovada = data?.ownedBy;
  const canManage = podeGerir && daAprovada === undefined;

  const roleById = useMemo(
    () => new Map((data?.roles ?? []).map((role) => [role.id, role])),
    [data],
  );
  const teamByEmployee = useMemo(
    () =>
      new Map(
        (data?.members ?? [])
          .filter((member) => member.leftAt === undefined)
          .map((member) => [member.employeeId, member.teamId]),
      ),
    [data],
  );
  const unitNameById = useMemo(
    () => new Map((units ?? []).map((unit) => [unit.id as string, unit.name])),
    [units],
  );

  const teamById = useMemo(
    () => new Map((data?.teams ?? []).map((team) => [team.id, team])),
    [data],
  );

  const employees = useMemo(() => {
    const term = normalizeName(search);
    const rows = data?.employees ?? [];
    if (term === '') return rows;
    return rows.filter(
      (employee) =>
        normalizeName(employee.name).includes(term) ||
        normalizeName(roleById.get(employee.jobRoleId)?.name ?? '').includes(term),
    );
  }, [data, search, roleById]);

  const closeEmployeeDialog = () => {
    setEmployeeOpen(false);
    setEditingEmployeeId(undefined);
  };
  const closeTeamDialog = () => {
    setTeamOpen(false);
    setEditingTeamId(undefined);
  };
  const closeServiceDialog = () => {
    setServiceOpen(false);
    setEditingServiceId(undefined);
  };

  const [createEmployee, employeeState] = useCommand(
    (input: EmployeeSubmit) =>
      container.createEmployee.execute({ ...input, projectId: activeProjectId }, session!),
    {
      successMessage: 'Funcionário cadastrado.',
      silent: true,
      onSuccess: () => closeEmployeeDialog(),
    },
  );

  const [updateEmployee, updateEmployeeState] = useCommand(
    (input: EmployeeSubmit & { employeeId: string }) =>
      container.updateEmployee.execute(input, session!),
    {
      successMessage: 'Funcionário atualizado.',
      silent: true,
      onSuccess: () => closeEmployeeDialog(),
    },
  );

  // Situação é ato próprio, com data e auditoria próprias (RN-058) — por isso é um controle
  // à parte, não um campo no meio da edição de nome e telefone.
  const [changeStatus] = useCommand(
    (input: { employeeId: string; status: 'ACTIVE' | 'ON_LEAVE' | 'TERMINATED' }) =>
      container.changeEmployeeStatus.execute(input, session!),
    { successMessage: 'Situação atualizada.' },
  );

  const [createTeam, teamState] = useCommand(
    (name: string) => container.createTeam.execute({ projectId: activeProjectId, name }, session!),
    { successMessage: 'Equipe criada.', silent: true, onSuccess: () => closeTeamDialog() },
  );

  const [updateTeam, updateTeamState] = useCommand(
    (input: { teamId: string; name: string; leaderEmployeeId: string | null }) =>
      container.updateTeam.execute(input, session!),
    { successMessage: 'Equipe atualizada.', silent: true, onSuccess: () => closeTeamDialog() },
  );

  // RN-433 — a lista inteira; a vazia volta ao padrão, a maioria dos membros do dia.
  const [setTeamDisciplines, teamDisciplinesState] = useCommand(
    (input: { teamId: string; disciplineIds: readonly string[] }) =>
      container.setTeamDisciplines.execute(
        { teamId: input.teamId, disciplineIds: [...input.disciplineIds] },
        session!,
      ),
    {
      successMessage: (marcadas) =>
        marcadas.length === 0
          ? 'A equipe voltou ao padrão: o que mais da metade dela executa.'
          : 'Disciplinas da equipe atualizadas.',
      silent: true,
      onSuccess: () => setDisciplinesTeamId(undefined),
    },
  );

  const [createService, serviceState] = useCommand(
    (input: ServiceSubmit) =>
      container.createExternalService.execute({ ...input, projectId: activeProjectId }, session!),
    { successMessage: 'Serviço cadastrado.', silent: true, onSuccess: () => closeServiceDialog() },
  );

  const [updateService, updateServiceState] = useCommand(
    (input: ServiceSubmit & { serviceId: string }) =>
      container.updateExternalService.execute(input, session!),
    { successMessage: 'Serviço atualizado.', silent: true, onSuccess: () => closeServiceDialog() },
  );

  const [runPreview, previewState] = useCommand(
    (input: {
      bytes: Uint8Array;
      fileName: string;
      mapping?: EmployeeColumnMapping;
      createMissingRoles?: boolean;
    }) =>
      container.previewEmployeeImport.execute({ ...input, projectId: activeProjectId! }, session!),
    { silent: true, onSuccess: (value) => setPreview(value) },
  );

  const [confirmImport, confirmState] = useCommand(
    (options: EmployeeImportOptions) =>
      container.confirmEmployeeImport.execute(
        {
          projectId: activeProjectId!,
          rows: preview?.rows ?? [],
          fileName: preview?.fileName ?? '',
          ...options,
        },
        session!,
      ),
    {
      successMessage: (result) =>
        `${String(result.created)} funcionários cadastrados · ${String(result.updated)} atualizados${result.teamsCreated > 0 ? ` · ${String(result.teamsCreated)} equipes criadas` : ''}.`,
      silent: true,
      onSuccess: () => {
        setImportOpen(false);
        setPreview(undefined);
        setFileBytes(undefined);
      },
    },
  );

  const handleImportFile = (file: File): void => {
    void file.arrayBuffer().then((buffer) => {
      const bytes = new Uint8Array(buffer);
      setFileBytes({ bytes, name: file.name });
      void runPreview({ bytes, fileName: file.name });
    });
  };

  const handleRemap = (mapping: EmployeeColumnMapping, createMissingRoles: boolean): void => {
    if (fileBytes === undefined) return;
    void runPreview({
      bytes: fileBytes.bytes,
      fileName: fileBytes.name,
      mapping,
      createMissingRoles,
    });
  };

  /**
   * O cadastro que faltava.
   *
   * `CreateJobRole` existia no container e como endpoint desde sempre, e **nenhuma tela o
   * chamava**: a única função que nascia era a que a importação de planilha criava pelo
   * rótulo da coluna. Como a função é obrigatória no cadastro do funcionário, quem não
   * tinha planilha não conseguia cadastrar nem uma coisa nem outra.
   */
  const [criarFuncao, funcaoState] = useCommand(
    (input: { name: string; code: string | undefined }) =>
      container.createJobRole.execute(
        { name: input.name, ...(input.code === undefined ? {} : { code: input.code }) },
        session!,
      ),
    { successMessage: 'Função criada.', silent: true, onSuccess: () => setRoleOpen(false) },
  );

  const [setRoleDisciplines] = useCommand(
    async (changes: readonly RoleDisciplinesChange[]) => {
      // Um lote — arrastar, "todas as funções" — são várias funções: grava uma por vez, para
      // na primeira recusa e avisa uma vez só no fim.
      for (const change of changes) {
        const resultado = await container.setJobRoleDisciplines.execute(
          { jobRoleId: change.jobRoleId, disciplineIds: [...change.disciplineIds] },
          session!,
        );
        if (!resultado.ok) return resultado;
      }
      return ok(changes.length);
    },
    // Sem `silent`: a matriz não tem onde mostrar erro, então a recusa precisa aparecer no
    // aviso — do contrário o clique simplesmente não faz nada.
    {
      successMessage: (funcoes) =>
        funcoes === 1
          ? 'Disciplinas da função atualizadas.'
          : `Disciplinas de ${String(funcoes)} funções atualizadas.`,
    },
  );

  const [moveEmployee] = useCommand(
    (employeeId: string, teamId: string | undefined) =>
      container.moveEmployeeToTeam.execute({ employeeId, teamId }, session!),
    { successMessage: 'Equipe atualizada.' },
  );

  /**
   * Trocar de equipe, e dizer o que o planejamento fez junto (Fase 30, RN-426).
   *
   * Na obra por equipe, quem sai perde e quem entra ganha as alocações da equipe nos dias
   * seguintes. O dia que não coube na jornada de quem entrou ficou de fora — e é esse que
   * precisa ser dito, com a data e a frente, para alguém decidir o que fazer com ele.
   */
  const trocarEquipe = async (employeeId: string, name: string, teamId: string | undefined) => {
    const result = await moveEmployee(employeeId, teamId);
    if (result === undefined || !result.ok) return;
    const aviso = teamMoveNotice(result.value.planning, name);
    if (aviso === undefined) return;
    if (aviso.tone === 'warning') toast.warning(aviso.message);
    else toast.info(aviso.message);
  };

  // Os rascunhos saem da lista viva, não de uma cópia guardada ao abrir o diálogo (que
  // mostraria o valor antigo). Tudo vira texto aqui; a volta a número/data/CPF é no envio.
  const editingEmployee = data?.employees.find((item) => item.id === editingEmployeeId);
  const employeeDraft =
    editingEmployee === undefined
      ? undefined
      : {
          name: editingEmployee.name,
          jobRoleId: editingEmployee.jobRoleId as string,
          ...(editingEmployee.cpf === undefined ? {} : { cpf: editingEmployee.cpf }),
          ...(editingEmployee.registration === undefined
            ? {}
            : { registration: editingEmployee.registration }),
          // Com vírgula: o campo lê "3.520,50" à brasileira, e o ponto de "3520.5" era lido
          // como milhar — o salário com centavos voltava multiplicado por dez.
          ...(editingEmployee.monthlySalary === undefined
            ? {}
            : { monthlySalary: numeroNoCampo(editingEmployee.monthlySalary.toUnits()) }),
          // O encargo volta ao campo para voltar no envio: a edição troca o cadastro inteiro,
          // e o encargo que não fosse reenviado seria apagado (D12).
          ...(editingEmployee.chargesPercent === undefined
            ? {}
            : { chargesPercent: numeroNoCampo(editingEmployee.chargesPercent) }),
          ...(editingEmployee.admittedAt === undefined
            ? {}
            : { admittedAt: editingEmployee.admittedAt }),
          ...(editingEmployee.phone === undefined ? {} : { phone: editingEmployee.phone }),
          ...(editingEmployee.notes === undefined ? {} : { notes: editingEmployee.notes }),
        };

  const editingTeam = data?.teams.find((item) => item.id === editingTeamId);
  const teamDraft =
    editingTeam === undefined
      ? undefined
      : {
          name: editingTeam.name,
          ...(editingTeam.leaderEmployeeId === undefined
            ? {}
            : { leaderEmployeeId: editingTeam.leaderEmployeeId as string }),
        };
  const disciplinesOfTeam = new Map(
    (data?.teamDisciplines ?? []).map((item) => [item.teamId as string, item]),
  );
  const disciplinesTeam = data?.teams.find((item) => item.id === disciplinesTeamId);
  const teamCandidates = (data?.employees ?? [])
    .filter((employee) => teamByEmployee.get(employee.id) === editingTeamId)
    .map((employee) => ({ id: employee.id, name: employee.name }));

  const editingService = data?.services.find((item) => item.id === editingServiceId);
  const serviceDraft =
    editingService === undefined
      ? undefined
      : {
          name: editingService.name,
          pricingMode: editingService.pricingMode,
          isActive: editingService.isActive,
          ...(editingService.company === undefined ? {} : { company: editingService.company }),
          ...(editingService.contact === undefined ? {} : { contact: editingService.contact }),
          ...(editingService.dailyCapacityQuarterHours === undefined
            ? {}
            : { dailyCapacityHours: numeroNoCampo(editingService.dailyCapacityQuarterHours / 4) }),
          ...(editingService.hourlyCost === undefined
            ? {}
            : { hourlyCost: numeroNoCampo(editingService.hourlyCost.toUnits()) }),
          ...(editingService.unitOfMeasureId === undefined
            ? {}
            : { unitOfMeasureId: editingService.unitOfMeasureId as string }),
          ...(editingService.unitCost === undefined
            ? {}
            : { unitCost: numeroNoCampo(editingService.unitCost.toUnits()) }),
        };

  if (activeProjectId === undefined) {
    return (
      <EmptyState
        title="Nenhuma obra selecionada"
        description="Escolha uma obra no seletor do topo para ver a equipe."
        icon={<HardHat className="size-8" />}
      />
    );
  }

  if (view === undefined) return <LoadingState label="Carregando a equipe…" />;
  if (!view.ok) return <ErrorState message={view.error.message} code={view.error.code} />;
  if (data === undefined) return <LoadingState />;

  const capacity = calendar !== undefined && calendar.ok ? calendar.value.capacity : undefined;

  /**
   * Mês de referência do custo por hora (RN-059): o custo é salário com encargos ÷ horas
   * úteis daquele mês, e vira quando o mês vira. A tela nomeia o mês para o valor não
   * parecer erro.
   */
  const costMonth =
    calendar !== undefined && calendar.ok ? mesPorExtenso(calendar.value.today) : undefined;

  /**
   * O quadro de efetivo: os números que o escritório da obra pergunta antes de abrir a
   * lista. Folha e custo médio saem do que está cadastrado — quem não tem salário fica
   * fora da conta, e a dica diz quantos são.
   *
   * Com os encargos (Fase 29, D12): a folha do mês é o que a obra paga, e não a soma dos
   * salários — e o custo médio da hora é a mesma conta que o orçamento usa na função (D11).
   */
  const active = data.employees.filter((employee) => employee.status === 'ACTIVE');
  const onLeave = data.employees.filter((employee) => employee.status === 'ON_LEAVE').length;
  const inTeams = active.filter((employee) => teamByEmployee.has(employee.id)).length;
  const activeServices = data.services.filter((service) => service.isActive).length;
  const inactiveServices = data.services.length - activeServices;
  const folha = custoDaFolha(data.employees, capacity?.monthly ?? 0);
  const month = costMonth ?? 'do mês';

  const kpis: readonly KpiItem[] = [
    {
      label: 'Efetivo',
      value: String(active.length),
      hint:
        onLeave > 0
          ? `${String(data.employees.length)} no cadastro · ${String(onLeave)} ${onLeave === 1 ? 'afastado' : 'afastados'}`
          : `${String(data.employees.length)} no cadastro`,
    },
    {
      label: 'Equipes',
      value: String(data.teams.length),
      hint:
        active.length === 0
          ? 'Ninguém para agrupar'
          : `${String(inTeams)} de ${String(active.length)} ativos em equipe`,
    },
    {
      label: 'Terceirizados',
      value: String(activeServices),
      hint:
        inactiveServices > 0
          ? `${String(inactiveServices)} ${inactiveServices === 1 ? 'inativo' : 'inativos'}`
          : 'Empreiteiras e serviços contratados',
    },
    {
      // O mês vai na dica, não no rótulo: "outubro/2026" no título quebrava a régua em duas
      // linhas e desalinhava os cinco números.
      label: 'Capacidade do mês',
      value: capacity === undefined ? '—' : formatQuarterHours(capacity.monthly),
      hint:
        capacity === undefined
          ? 'Sem calendário na obra'
          : `${costMonth ?? 'Este mês'} · ${String(capacity.workingDays)} dias úteis por pessoa`,
    },
    {
      // Com encargos (D12): é o custo da folha, e a mesma conta que o orçamento põe na hora.
      label: 'Folha do mês',
      value: folha.comSalario === 0 ? '—' : folha.custoDoMes.format(),
      hint:
        folha.comSalario === 0
          ? 'Nenhum salário cadastrado'
          : [
              folha.mediaPorHora === undefined
                ? `${costMonth ?? 'Este mês'} · com encargos`
                : `${costMonth ?? 'Este mês'} · ${folha.mediaPorHora.format()}/h em média, com encargos`,
              folha.comSalario < active.length
                ? `${String(active.length - folha.comSalario)} sem salário`
                : undefined,
              folha.semEncargo > 0 ? `${String(folha.semEncargo)} sem encargo` : undefined,
            ]
              .filter((parte) => parte !== undefined)
              .join(' · '),
    },
  ];

  const headcountByRole = new Map<JobRoleId, number>();
  for (const employee of active) {
    headcountByRole.set(employee.jobRoleId, (headcountByRole.get(employee.jobRoleId) ?? 0) + 1);
  }
  const roleHeadcounts = [...headcountByRole.entries()]
    .map(([id, count]) => ({ id, name: roleById.get(id)?.name ?? '—', count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Mão de obra</h1>
          <p className="text-(--color-content-muted)">
            Quem executa a obra — funcionários e serviços terceirizados.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canManage && tab === 'employees' && (
            <Button variant="secondary" onClick={() => setImportOpen(true)}>
              <Upload aria-hidden="true" />
              Importar planilha
            </Button>
          )}
          {canManage && (
            <Button
              variant="primary"
              onClick={() => {
                if (tab === 'employees') setEmployeeOpen(true);
                else if (tab === 'teams') setTeamOpen(true);
                else if (tab === 'roles') setRoleOpen(true);
                else setServiceOpen(true);
              }}
            >
              <Plus aria-hidden="true" />
              {tab === 'employees'
                ? 'Novo funcionário'
                : tab === 'teams'
                  ? 'Nova equipe'
                  : tab === 'roles'
                    ? 'Nova função'
                    : 'Novo serviço'}
            </Button>
          )}
        </div>
      </header>

      {daAprovada !== undefined && (
        <div
          role="status"
          className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-(--color-state-warn) bg-(--color-state-warn)/10 px-4 py-3 text-sm"
        >
          <FileClock className="size-4 shrink-0 text-(--color-state-warn)" aria-hidden="true" />
          <p className="min-w-0 flex-1">
            Esta obra é uma versão de orçamento. Os funcionários, as equipes e os terceirizados são
            os de {daAprovada.name} e valem para todas as versões: cadastre e edite lá.
          </p>
          <button
            type="button"
            className="font-medium underline underline-offset-2"
            onClick={() => void selectProject(daAprovada.projectId)}
          >
            Abrir {daAprovada.name}
          </button>
        </div>
      )}

      <KpiStrip items={kpis} columns={5} />

      {/*
        A composição do efetivo, função por função — o número que o encarregado sabe de
        cabeça e o escritório não. Cada função é também um filtro da lista: clicar em
        "Pedreiro" é a mesma coisa que digitá-lo na busca.
      */}
      {/* O atalho filtra a aba Funcionários: sem ela no papel, ele não tem para onde levar. */}
      {roleHeadcounts.length > 0 && tabs.some((item) => item.id === 'employees') && (
        <Section title="Por função" count={roleHeadcounts.length}>
          <ul className="flex flex-wrap gap-x-5 gap-y-1">
            {roleHeadcounts.map((role) => {
              const selected = normalizeName(search) === normalizeName(role.name);
              return (
                <li key={role.id}>
                  <button
                    type="button"
                    aria-pressed={selected}
                    title={selected ? 'Mostrar todos' : `Mostrar só ${role.name}`}
                    onClick={() => {
                      setSearch(selected ? '' : role.name);
                      setTab('employees');
                    }}
                    className={cn(
                      '-mx-1.5 flex items-baseline gap-1.5 rounded-md px-1.5 py-1 transition-colors hover:bg-(--color-surface-muted)',
                      selected && 'bg-(--color-brand-soft) hover:bg-(--color-brand-soft)',
                    )}
                  >
                    <Figure size="md" value={String(role.count)} />
                    <span className="text-sm text-(--color-content-muted)">{role.name}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </Section>
      )}

      {/* ── Abas ──────────────────────────────────────────────────────────── */}
      <TabList
        base="mao-de-obra"
        items={tabs}
        atual={tab}
        onEscolher={setTab}
        label="Seções da mão de obra"
      />

      <TabPanel base="mao-de-obra" id={tab} className="flex flex-col gap-6">
        {tab === 'roles' && (
          <Card>
            <CardHeader>
              <CardTitle>Funções e disciplinas</CardTitle>
              <CardDescription>
                Marque o que cada função executa. É esse vínculo que deixa o planejamento propor as
                atividades sozinho — só as disciplinas ativas entram.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RoleDisciplinesPanel
                roles={data.roles}
                disciplines={data.disciplines}
                links={data.roleDisciplines}
                headcount={headcountByRole}
                canManage={canManage}
                onChange={(changes) => void setRoleDisciplines(changes)}
              />
            </CardContent>
          </Card>
        )}

        {tab === 'employees' && (
          <Card>
            <CardHeader className="flex-row flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                {/* O título conta: "16 funcionários", ou "4 de 16" enquanto a busca filtra. */}
                <CardTitle>
                  {employees.length === data.employees.length
                    ? `${String(data.employees.length)} ${data.employees.length === 1 ? 'funcionário' : 'funcionários'}`
                    : `${String(employees.length)} de ${String(data.employees.length)} funcionários`}
                </CardTitle>
                <CardDescription>
                  Custo/hora = salário × (1 + encargos) ÷ horas úteis {month}, refeito a cada virada
                  de mês.
                </CardDescription>
              </div>
              <div className="relative flex items-center">
                <Search
                  className="pointer-events-none absolute left-2.5 size-4 text-(--color-content-muted)"
                  aria-hidden="true"
                />
                <Input
                  aria-label="Buscar funcionário"
                  className="w-56 pl-8"
                  placeholder="Nome ou função…"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {employees.length === 0 ? (
                <EmptyState
                  title={data.employees.length === 0 ? 'Nenhum funcionário' : 'Nada encontrado'}
                  description={
                    data.employees.length === 0
                      ? 'Cadastre quem trabalha nesta obra para poder planejar a mão de obra.'
                      : 'Nenhum funcionário corresponde à busca.'
                  }
                  icon={<Users className="size-8" />}
                  {...(canManage && data.employees.length === 0
                    ? {
                        action: { label: 'Novo funcionário', onClick: () => setEmployeeOpen(true) },
                      }
                    : {})}
                />
              ) : (
                <TableWrapper>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nome</TableHead>
                        <TableHead>Função</TableHead>
                        <TableHead>Equipe</TableHead>
                        <TableHead className="text-right">Salário mensal</TableHead>
                        <TableHead className="text-right">
                          Custo/hora
                          {costMonth !== undefined && (
                            <span className="block text-[10px] font-normal normal-case text-(--color-content-muted)">
                              {costMonth}
                            </span>
                          )}
                        </TableHead>
                        <TableHead>Situação</TableHead>
                        {canManage && (
                          <TableHead className="text-right">
                            <span className="sr-only">Ações</span>
                          </TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {employees.map((employee) => {
                        const teamId = teamByEmployee.get(employee.id);
                        const cpf =
                          employee.cpf === undefined ? undefined : Cpf.parse(employee.cpf);
                        // O custo por hora é conta, não cadastro (RN-059): sai do salário do
                        // mês com os encargos (D12), dividido pelas horas úteis **deste** mês,
                        // que vêm do calendário.
                        const hourlyRate = employeeHourlyRate(employee, capacity?.monthly ?? 0);
                        const monthlyCost = monthlyCostWithCharges(
                          employee.monthlySalary,
                          employee.chargesPercent,
                        );

                        return (
                          <TableRow
                            key={employee.id}
                            // Quem saiu continua na lista, mas apagado: o histórico fica, a
                            // atenção vai para quem está na obra.
                            className={cn(
                              employee.status === 'TERMINATED' && 'text-(--color-content-muted)',
                            )}
                          >
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="font-medium">{employee.name}</span>
                                {cpf !== undefined && cpf.ok && (
                                  <span className="font-mono text-[11px] text-(--color-content-muted)">
                                    {cpf.value.format()}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>{roleById.get(employee.jobRoleId)?.name ?? '—'}</TableCell>
                            <TableCell>
                              {canManage ? (
                                <select
                                  aria-label={`Equipe de ${employee.name}`}
                                  className={QUIET_SELECT}
                                  value={teamId ?? ''}
                                  onChange={(event) =>
                                    void trocarEquipe(
                                      employee.id,
                                      employee.name,
                                      event.target.value === '' ? undefined : event.target.value,
                                    )
                                  }
                                >
                                  <option value="">— sem equipe —</option>
                                  {data.teams.map((team) => (
                                    <option key={team.id} value={team.id}>
                                      {team.name}
                                    </option>
                                  ))}
                                </select>
                              ) : teamId === undefined ? (
                                '—'
                              ) : (
                                (teamById.get(teamId)?.name ?? '—')
                              )}
                            </TableCell>
                            <TableCell className="tabular text-right">
                              {employee.monthlySalary?.format() ?? '—'}
                              {/*
                                O encargo ao lado do salário (D12). O ativo com salário e sem
                                encargo tem custo, só que menor do que a obra paga — dito
                                aqui, onde se corrige, e não descoberto no orçamento.
                              */}
                              {employee.monthlySalary !== undefined &&
                                employee.chargesPercent !== undefined && (
                                  <span className="block text-[11px] text-(--color-content-muted)">
                                    {`+ ${textoDoEncargo(employee.chargesPercent)} de encargos`}
                                  </span>
                                )}
                              {semEncargoInformado(employee) && (
                                <span className="block text-[11px] text-(--color-state-warn)">
                                  encargo não informado — o custo está sem encargos
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="tabular text-right">
                              {hourlyRate === undefined || monthlyCost === undefined ? (
                                <span className="text-(--color-content-muted)">—</span>
                              ) : (
                                <span
                                  title={`${
                                    employee.chargesPercent === undefined
                                      ? monthlyCost.format()
                                      : `${employee.monthlySalary!.format()} + ${textoDoEncargo(
                                          employee.chargesPercent,
                                        )} de encargos = ${monthlyCost.format()}`
                                  } ÷ ${Hours.ofQuarters(
                                    hourlyRate.monthlyQuarterHours,
                                  ).format()} úteis em ${costMonth ?? 'no mês'} — recalculado a cada mês`}
                                >
                                  {hourlyRate.perHour.format()}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              {canManage ? (
                                <span className="flex items-center gap-1.5">
                                  <StatusDot status={employee.status} />
                                  <select
                                    aria-label={`Situação de ${employee.name}`}
                                    className={QUIET_SELECT}
                                    value={employee.status}
                                    onChange={(event) =>
                                      void changeStatus({
                                        employeeId: employee.id,
                                        status: event.target.value as
                                          'ACTIVE' | 'ON_LEAVE' | 'TERMINATED',
                                      })
                                    }
                                  >
                                    {(['ACTIVE', 'ON_LEAVE', 'TERMINATED'] as const).map(
                                      (status) => (
                                        <option key={status} value={status}>
                                          {EMPLOYEE_STATUS_LABEL[status]}
                                        </option>
                                      ),
                                    )}
                                  </select>
                                </span>
                              ) : (
                                <Badge tone={STATUS_TONE[employee.status]}>
                                  {EMPLOYEE_STATUS_LABEL[employee.status]}
                                </Badge>
                              )}
                            </TableCell>
                            {canManage && (
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label={`Editar ${employee.name}`}
                                  title="Editar"
                                  onClick={() => {
                                    setEditingEmployeeId(employee.id);
                                    setEmployeeOpen(true);
                                  }}
                                >
                                  <Pencil aria-hidden="true" />
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableWrapper>
              )}
            </CardContent>
          </Card>
        )}

        {tab === 'teams' && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.teams.length === 0 ? (
              <Card className="sm:col-span-2 lg:col-span-3">
                <CardContent className="p-0">
                  <EmptyState
                    title="Nenhuma equipe"
                    description="Agrupe os funcionários em equipes para planejar por frente de serviço."
                    icon={<Users className="size-8" />}
                    {...(canManage
                      ? { action: { label: 'Nova equipe', onClick: () => setTeamOpen(true) } }
                      : {})}
                  />
                </CardContent>
              </Card>
            ) : (
              data.teams.map((team) => {
                const members = data.employees.filter(
                  (employee) => teamByEmployee.get(employee.id) === team.id,
                );
                // O que a equipe custa por hora: a soma de quem tem salário cadastrado, com
                // os encargos de cada um (D12).
                const teamCost =
                  capacity === undefined
                    ? undefined
                    : custoDaEquipePorHora(members, capacity.monthly);

                return (
                  <Card key={team.id}>
                    <CardHeader className="flex-row items-start justify-between gap-2">
                      <div className="min-w-0">
                        <CardTitle className="text-base">{team.name}</CardTitle>
                        <CardDescription>
                          {members.length === 0
                            ? 'Sem integrantes'
                            : `${String(members.length)} ${members.length === 1 ? 'integrante' : 'integrantes'}`}
                          {teamCost !== undefined && ` · ${teamCost.format()}/h`}
                        </CardDescription>
                      </div>
                      {canManage && (
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Editar ${team.name}`}
                          onClick={() => {
                            setEditingTeamId(team.id);
                            setTeamOpen(true);
                          }}
                        >
                          <Pencil aria-hidden="true" />
                        </Button>
                      )}
                    </CardHeader>
                    <CardContent>
                      {members.length === 0 ? (
                        <p className="text-sm text-(--color-content-muted)">
                          Sem integrantes. Atribua a equipe na aba Funcionários.
                        </p>
                      ) : (
                        <ul className="-mx-2 flex flex-col divide-y divide-(--color-border-subtle) text-sm">
                          {/* O líder vem primeiro, nomeado; quem está afastado diz isso em
                              palavra, não só em cor. */}
                          {[...members]
                            .sort((a, b) =>
                              a.id === team.leaderEmployeeId
                                ? -1
                                : b.id === team.leaderEmployeeId
                                  ? 1
                                  : a.name.localeCompare(b.name),
                            )
                            .map((employee) => (
                              <li key={employee.id} className="flex items-center gap-2 px-2 py-1.5">
                                <span className="min-w-0 flex-1 truncate">{employee.name}</span>
                                {employee.id === team.leaderEmployeeId && (
                                  <Badge tone="info">líder</Badge>
                                )}
                                {employee.status !== 'ACTIVE' && (
                                  <Badge tone={STATUS_TONE[employee.status]}>
                                    {EMPLOYEE_STATUS_LABEL[employee.status]}
                                  </Badge>
                                )}
                                <span className="shrink-0 text-xs text-(--color-content-muted)">
                                  {roleById.get(employee.jobRoleId)?.name ?? '—'}
                                </span>
                              </li>
                            ))}
                        </ul>
                      )}
                      <TeamDisciplinesSection
                        teamName={team.name}
                        {...teamDisciplinesSummary({
                          disciplines: data.disciplines,
                          marked: disciplinesOfTeam.get(team.id)?.marked ?? [],
                          majority: disciplinesOfTeam.get(team.id)?.majority ?? [],
                        })}
                        onOpen={() => setDisciplinesTeamId(team.id)}
                      />
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {tab === 'services' && (
          <Card>
            <CardHeader>
              <CardTitle>
                {`${String(data.services.length)} ${data.services.length === 1 ? 'serviço terceirizado' : 'serviços terceirizados'}`}
              </CardTitle>
              <CardDescription>
                Empreiteiras e serviços contratados. No planejamento entram como a equipe própria;
                em compras, não — serviço não é material.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {data.services.length === 0 ? (
                <EmptyState
                  title="Nenhum serviço terceirizado"
                  description="Cadastre empreiteiras e serviços contratados para alocá-los no planejamento."
                  icon={<HardHat className="size-8" />}
                  {...(canManage
                    ? { action: { label: 'Novo serviço', onClick: () => setServiceOpen(true) } }
                    : {})}
                />
              ) : (
                <TableWrapper>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Serviço</TableHead>
                        <TableHead>Empresa</TableHead>
                        <TableHead>Capacidade diária</TableHead>
                        <TableHead>Contratação</TableHead>
                        <TableHead className="text-right">Preço</TableHead>
                        <TableHead>Situação</TableHead>
                        {canManage && (
                          <TableHead className="text-right">
                            <span className="sr-only">Ações</span>
                          </TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.services.map((service) => (
                        <TableRow key={service.id}>
                          <TableCell className="font-medium">{service.name}</TableCell>
                          <TableCell className="text-(--color-content-muted)">
                            {service.company ?? '—'}
                          </TableCell>
                          <TableCell>
                            {service.dailyCapacityQuarterHours === undefined ? (
                              <span className="text-(--color-content-muted)">Sem limite</span>
                            ) : (
                              formatQuarterHours(service.dailyCapacityQuarterHours)
                            )}
                          </TableCell>
                          <TableCell className="text-(--color-content-muted)">
                            {SERVICE_PRICING_LABEL[service.pricingMode]}
                          </TableCell>
                          <TableCell className="tabular text-right">
                            {/* Cada modo mostra o seu preço: uma coluna "custo/hora" para quem
                              cobra por m² só mostraria "—", lido como "sem preço". */}
                            {service.pricingMode === 'UNIT'
                              ? service.unitCost === undefined
                                ? '—'
                                : `${service.unitCost.format()} / ${
                                    unitNameById.get(service.unitOfMeasureId ?? '') ?? 'un'
                                  }`
                              : (service.hourlyCost?.format() ?? '—')}
                          </TableCell>
                          <TableCell>
                            {service.isActive ? (
                              <Badge tone="ok">Ativo</Badge>
                            ) : (
                              <Badge tone="neutral">Inativo</Badge>
                            )}
                          </TableCell>
                          {canManage && (
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`Editar ${service.name}`}
                                title="Editar"
                                onClick={() => {
                                  setEditingServiceId(service.id);
                                  setServiceOpen(true);
                                }}
                              >
                                <Pencil aria-hidden="true" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableWrapper>
              )}
            </CardContent>
          </Card>
        )}
      </TabPanel>

      <EmployeeImportDialog
        open={importOpen}
        onOpenChange={(open) => {
          setImportOpen(open);
          if (!open) {
            setPreview(undefined);
            setFileBytes(undefined);
          }
        }}
        preview={preview}
        onFile={handleImportFile}
        onRemap={handleRemap}
        onConfirm={(options) => void confirmImport(options)}
        isPending={previewState.isPending || confirmState.isPending}
        errorMessage={previewState.error?.message ?? confirmState.error?.message}
      />

      <EmployeeDialog
        open={employeeOpen}
        onOpenChange={(open) => {
          if (open) setEmployeeOpen(true);
          else closeEmployeeDialog();
        }}
        {...(employeeDraft === undefined ? {} : { employee: employeeDraft })}
        roles={data.roles}
        onSubmit={(input) => {
          // O mesmo formulário atende os dois caminhos; quem decide é ter ou não um id.
          if (editingEmployeeId === undefined) void createEmployee(input);
          else void updateEmployee({ ...input, employeeId: editingEmployeeId });
        }}
        isPending={employeeState.isPending || updateEmployeeState.isPending}
        errorMessage={
          editingEmployeeId === undefined
            ? employeeState.error?.message
            : updateEmployeeState.error?.message
        }
      />

      <JobRoleDialog
        open={roleOpen}
        onOpenChange={setRoleOpen}
        onSubmit={(input) => void criarFuncao(input)}
        isPending={funcaoState.isPending}
        errorMessage={funcaoState.error?.message}
      />

      <TeamDialog
        open={teamOpen}
        onOpenChange={(open) => {
          if (open) setTeamOpen(true);
          else closeTeamDialog();
        }}
        {...(teamDraft === undefined ? {} : { team: teamDraft })}
        members={teamCandidates}
        onSubmit={(input) => {
          if (editingTeamId === undefined) void createTeam(input.name);
          else void updateTeam({ ...input, teamId: editingTeamId });
        }}
        isPending={teamState.isPending || updateTeamState.isPending}
        errorMessage={
          editingTeamId === undefined ? teamState.error?.message : updateTeamState.error?.message
        }
      />

      {disciplinesTeam !== undefined && (
        <TeamDisciplinesDialog
          open
          onOpenChange={(open) => {
            if (!open) setDisciplinesTeamId(undefined);
          }}
          teamName={disciplinesTeam.name}
          disciplines={data?.disciplines ?? []}
          marked={disciplinesOfTeam.get(disciplinesTeam.id)?.marked ?? []}
          majority={disciplinesOfTeam.get(disciplinesTeam.id)?.majority ?? []}
          canManage={canManage}
          isPending={teamDisciplinesState.isPending}
          {...(teamDisciplinesState.error === undefined
            ? {}
            : { errorMessage: teamDisciplinesState.error.message })}
          onSubmit={(disciplineIds) =>
            void setTeamDisciplines({ teamId: disciplinesTeam.id, disciplineIds })
          }
        />
      )}

      <ServiceDialog
        open={serviceOpen}
        onOpenChange={(open) => {
          if (open) setServiceOpen(true);
          else closeServiceDialog();
        }}
        units={units ?? []}
        {...(serviceDraft === undefined ? {} : { service: serviceDraft })}
        onSubmit={(input) => {
          if (editingServiceId === undefined) void createService(input);
          else void updateService({ ...input, serviceId: editingServiceId });
        }}
        isPending={serviceState.isPending || updateServiceState.isPending}
        errorMessage={
          editingServiceId === undefined
            ? serviceState.error?.message
            : updateServiceState.error?.message
        }
      />
    </div>
  );
}

export { RESOURCE_TYPE_LABEL };
